/**
 * Gray shimmer placeholders shown while a component's data is still loading.
 * Each composed skeleton mirrors the DOM shape of the thing it stands in for
 * (see the matching `.card`, `.now`, `.panel` rules in index.css) so the page
 * doesn't jump once real content replaces it.
 */
import type { CSSProperties, ReactNode } from "react";

/** A single gray block. The building unit every skeleton below is made of. */
export function Skeleton({
  width,
  height = "1em",
  radius,
  style,
}: {
  width?: string | number;
  height?: string | number;
  radius?: string | number;
  style?: CSSProperties;
}) {
  return (
    <span
      className="skeleton"
      style={{ width, height, borderRadius: radius, ...style }}
    />
  );
}

/** Wraps a skeleton so screen readers announce "Loading" instead of silence. */
function LoadingRegion({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div role="status" aria-busy="true">
      <span className="sr-only">{label}</span>
      <div aria-hidden="true">{children}</div>
    </div>
  );
}

/** Stands in for one `.now` (now-playing) block. */
export function NowPlayingSkeleton() {
  return (
    <div className="now">
      <Skeleton width={160} height="90px" style={{ flexShrink: 0 }} />
      <div className="body">
        <Skeleton width={100} height={11} style={{ marginBottom: 10 }} />
        <Skeleton width="55%" height={17} style={{ marginBottom: 12 }} />
        <div style={{ display: "flex", gap: 8 }}>
          <Skeleton width={64} height={18} radius={99} />
          <Skeleton width={90} height={12} />
        </div>
      </div>
    </div>
  );
}

/** Stands in for one `.card` request row. */
export function RequestCardSkeleton() {
  return (
    <div className="card">
      <Skeleton width={86} height="48px" style={{ flexShrink: 0 }} />
      <div className="body">
        <Skeleton width="60%" height={14} style={{ marginBottom: 8 }} />
        <div style={{ display: "flex", gap: 8 }}>
          <Skeleton width={54} height={16} radius={99} />
          <Skeleton width={70} height={12} />
        </div>
      </div>
    </div>
  );
}

/** Stands in for one `.card.donation` row. */
export function DonationRowSkeleton() {
  return (
    <div className="card donation">
      <div className="body">
        <div className="meta">
          <Skeleton width={54} height={16} radius={99} />
          <Skeleton width={60} height={16} radius={99} />
          <Skeleton width={80} height={12} />
        </div>
        <Skeleton width="40%" height={12} style={{ marginTop: 8 }} />
      </div>
    </div>
  );
}

/** A section heading skeleton — matches `.section-head h2` + count. */
function SectionHeadSkeleton({ width = 70 }: { width?: number }) {
  return (
    <div className="section-head">
      <Skeleton width={width} height={13} />
    </div>
  );
}

/** Full Queue tab: now-playing + paid/free sections. */
export function QueueSkeleton() {
  return (
    <LoadingRegion label="Loading queue…">
      <NowPlayingSkeleton />
      <div className="section">
        <SectionHeadSkeleton width={40} />
        <RequestCardSkeleton />
        <RequestCardSkeleton />
        <RequestCardSkeleton />
      </div>
      <div className="section">
        <SectionHeadSkeleton width={40} />
        <RequestCardSkeleton />
        <RequestCardSkeleton />
      </div>
    </LoadingRegion>
  );
}

/** Donations tab: a handful of donation rows. */
export function DonationsSkeleton() {
  return (
    <LoadingRegion label="Loading donations…">
      <DonationRowSkeleton />
      <DonationRowSkeleton />
      <DonationRowSkeleton />
    </LoadingRegion>
  );
}

/** One `.field` (label + input/select) skeleton. */
function FieldSkeleton({ flex }: { flex?: string }) {
  return (
    <div className="field" style={{ flex }}>
      <Skeleton width={90} height={10} />
      <Skeleton height={34} />
    </div>
  );
}

/** Settings tab: queue-settings panel + chat-ingestion panel. */
export function SettingsSkeleton() {
  return (
    <LoadingRegion label="Loading settings…">
      <div className="panel">
        <Skeleton width={130} height={13} style={{ marginBottom: 14 }} />
        <div className="row">
          <FieldSkeleton flex="0 0 140px" />
          <FieldSkeleton flex="0 1 210px" />
          <FieldSkeleton flex="0 1 210px" />
        </div>
      </div>
      <div className="panel">
        <Skeleton width={160} height={13} style={{ marginBottom: 14 }} />
        <div className="row">
          <FieldSkeleton flex="1 1 200px" />
        </div>
      </div>
    </LoadingRegion>
  );
}

/** StreamView tab: overlay canvas + control rows + OBS URL field. */
export function StreamViewSkeleton() {
  return (
    <LoadingRegion label="Loading StreamView…">
      <Skeleton width={140} height={17} style={{ marginBottom: 10 }} />
      <Skeleton width="60%" height={13} style={{ marginBottom: 16 }} />
      <div className="streamview-canvas skeleton" style={{ backgroundImage: "none" }} />
      <div className="panel-group row">
        <FieldSkeleton flex="0 0 180px" />
        <FieldSkeleton flex="0 0 180px" />
        <FieldSkeleton flex="0 0 200px" />
        <FieldSkeleton flex="0 0 200px" />
      </div>
      <div className="panel-group field">
        <Skeleton width={200} height={13} />
      </div>
      <div className="panel-group field grow">
        <Skeleton width={140} height={10} style={{ marginBottom: 6 }} />
        <Skeleton height={34} />
      </div>
    </LoadingRegion>
  );
}

/** Account tab: the connections panel (Twitch + YouTube rows). */
export function ConnectionsSkeleton() {
  return (
    <LoadingRegion label="Loading account…">
      <div className="panel">
        <Skeleton width={110} height={13} style={{ marginBottom: 14 }} />
        <Skeleton width="70%" height={13} style={{ marginBottom: 16 }} />
        <ul className="connections">
          <li className="connection-row">
            <Skeleton width={64} height={20} radius={99} />
            <div className="connection-detail">
              <Skeleton width={100} height={12} />
            </div>
          </li>
          <li className="connection-row">
            <Skeleton width={64} height={20} radius={99} />
            <div className="connection-detail">
              <Skeleton width={100} height={12} />
            </div>
          </li>
        </ul>
      </div>
    </LoadingRegion>
  );
}
